// Pagina Developer Settings — facciata di composizione (split free-win).
// I contenuti vivono in ./developer/: ApiKeysTab, WebhooksTab, docs/*.
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Key, Shield, Server, Globe, Code, BookOpen, Puzzle, Webhook } from 'lucide-react';
import { HubShell } from '@/components/common/HubShell';
import { useAuth } from '@/contexts/AuthContext';
import { Alert, AlertDescription, AlertTitle } from '@easyflow/ui-frontend/components/ui/alert';
import { SectionNav, type SectionNavSectionDef } from '@easyflow/ui-frontend/components/ui/section-nav';
import { useSectionNavState } from '@/hooks/useSectionNavState';
import MCPToolsTab from '@/components/mcp/MCPToolsTab';
import MCPGatewayTab from '@/components/mcp/MCPGatewayTab';
import IntegrationTemplatesTab from '@/components/mcp/IntegrationTemplatesTab';
import { ApiKeysTab } from '@/pages/developer/ApiKeysTab';
import { WebhooksTab } from '@/pages/developer/WebhooksTab';
import { DocumentationTab } from '@/pages/developer/docs/DocumentationTab';

const API_SECTIONS: SectionNavSectionDef[] = [
  { value: 'api-keys', label: 'API Keys', icon: Key },
  { value: 'webhooks', label: 'Webhooks', icon: Webhook },
];

function ApiSection() {
  const { value, setValue } = useSectionNavState({ sections: API_SECTIONS, sectionParam: 'api', subParam: 'api_sub' });
  return (
    <div className="w-full space-y-4">
      <SectionNav sections={API_SECTIONS} value={value} onChange={setValue} ariaLabel="Sezioni API" />
      <div className="pt-2">
        {value.section === 'api-keys' && <ApiKeysTab />}
        {value.section === 'webhooks' && <WebhooksTab />}
      </div>
    </div>
  );
}

const mainTabs = [
  { key: 'integrations',  label: 'Integrazioni',   mobileLabel: 'Integr.',  icon: Puzzle },
  { key: 'mcp-server',    label: 'MCP Server',     mobileLabel: 'Server',   icon: Server },
  { key: 'mcp-gateway',   label: 'MCP Gateway',    mobileLabel: 'Gateway',  icon: Globe },
  { key: 'api',           label: 'API',            mobileLabel: 'API',      icon: Code },
  { key: 'documentation', label: 'Documentation',  mobileLabel: 'Docs',     icon: BookOpen },
] as const;

type MainTabKey = typeof mainTabs[number]['key'];

export function DeveloperSettings() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') || 'integrations';
  const activeTab: MainTabKey = mainTabs.some(t => t.key === rawTab)
    ? (rawTab as MainTabKey)
    : 'integrations';

  const handleTabChange = useCallback((tabKey: MainTabKey) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tabKey);
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);

  if (user?.role !== 'admin') {
    return (
      <div className="space-y-6 p-4">
        <Alert variant="destructive">
          <Shield className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            Only tenant administrators can access Developer Settings and manage API keys.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const renderActive = () => {
    switch (activeTab) {
      case 'integrations':  return <IntegrationTemplatesTab />;
      case 'mcp-server':    return <MCPToolsTab />;
      case 'mcp-gateway':   return <MCPGatewayTab />;
      case 'documentation': return <DocumentationTab />;
      case 'api':
        return <ApiSection />;
      default: return null;
    }
  };

  return (
    <HubShell
      icon={Code}
      title="Developer Settings"
      subtitle="Manage API keys, webhooks, and integrations for your tenant"
      tabs={mainTabs}
      activeTab={activeTab}
      onTabChange={handleTabChange}
    >
      {renderActive()}
    </HubShell>
  );
}
